import { useState } from "react";
import { useFieldArray } from "react-hook-form";
import { toast } from "react-hot-toast";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { X } from "lucide-react";
import moment from "moment";

import style from "./ProstateAdditionalInfoForm.module.css";

const PSA_VALUE_PATTERN = /^\d*\.?\d*$/;

const toLatestPsaEntry = (latestPsaResult) => {
  if (!latestPsaResult?.value || !latestPsaResult?.date) return null;
  if (!PSA_VALUE_PATTERN.test(latestPsaResult.value)) return null;

  const parsedDate = dayjs(latestPsaResult.date);
  if (!parsedDate.isValid()) return null;

  return {
    psa: latestPsaResult.value,
    date: parsedDate.format("DD-MM-YYYY"),
  };
};

export const PsaFormItem = ({ form, latestPsaResult }) => {
  const [internalFormValues, setInternalFormValues] = useState({
    psa: "",
    date: "",
  });
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "psaTests",
  });

  const psaTestsError =
    form.formState.errors?.psaTests?.root || form.formState.errors?.psaTests;

  const isMaxItemsReached = fields.length >= 99;

  const latestPsaEntry = toLatestPsaEntry(latestPsaResult);
  const isLatestPsaImported =
    !!latestPsaEntry &&
    fields.some(
      (field) =>
        field.psa === latestPsaEntry.psa && field.date === latestPsaEntry.date
    );

  const handleImportLatestPsa = () => {
    if (!latestPsaResult) {
      toast.error("No PSA results on file for this patient");
      return;
    }

    if (!latestPsaEntry) {
      toast.error(
        `Latest PSA result on file (${latestPsaResult.value}) is not a valid number and cannot be imported`
      );
      return;
    }

    if (isLatestPsaImported) {
      toast.error("Latest PSA result is already added");
      return;
    }

    append(latestPsaEntry);
  };

  return (
    <div className={style.formItemWrapper}>
      <label className={style.addReferralFormLabel}>PSA Test</label>
      <div></div>
      <div className={style.psaTestsContainer} style={{ maxWidth: "500px" }}>
        <p style={{ fontSize: "12px", lineHeight: "15px", marginLeft: "-8px" }}>
          All men with abnormal Digital Rectal Exemination (DRE) should be
          referred to a urologist regardless of PSA results. Please wait six
          weeks to do a PSA test if a patient has had an active urinary
          infection, prostate biopsy, TURP, or prostatitie In a man with a
          normal DRE. repeat an abnormal PSA test at 6 weeks before referral.
        </p>
        <div
          style={{
            display: "flex",
            gap: "12px",
            marginLeft: "-20px",
            alignItems: "center",
          }}
        >
          <div style={{ marginTop: "5px", color: "#F72924" }}>*</div>
          <div className={style.psaTable}>
            <p>Total PSA (ng/ml)</p>
            <p>Date</p>
            <p>
              <X width={18} height={20} />
            </p>
          </div>
          <button
            type="button"
            onClick={handleImportLatestPsa}
            className={style.addPsaTestButton}
            style={{ whiteSpace: "nowrap" }}
            title={
              latestPsaEntry
                ? `Latest PSA on file: ${latestPsaEntry.psa} (${latestPsaEntry.date})`
                : "Import the latest PSA result from the patient's file"
            }
          >
            Import latest PSA
          </button>
        </div>
        <div className={style.psaTestEntry}>
          {fields.map((field, index) => (
            <div className={style.psaTestInputs} key={field.id}>
              <p style={{ width: "160px" }}>{field.psa}</p>
              <p style={{ width: "160px" }}>{field.date}</p>
              <button
                type="button"
                onClick={() => remove(index)}
                style={{
                  width: "32px",
                  height: "24px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
                className={style.addPsaTestButton}
                aria-label="Remove psa test"
              >
                <X width={12} height={12} />
              </button>
            </div>
          ))}
          {psaTestsError && (
            <p
              style={{
                color: "#b70000",
                fontSize: "13px",
                fontWeight: 400,
                lineHeight: "20px",
                marginTop: "5px",
              }}
            >
              {psaTestsError.message}
            </p>
          )}
        </div>

        {isMaxItemsReached ? (
          <p
            style={{
              fontSize: "13px",
              color: "#666",
              fontStyle: "italic",
              marginTop: "8px",
            }}
          >
            Maximum number of items is 99
          </p>
        ) : (
          <div className={style.psaFormWrapper}>
            <label className={style.psaLabel}>
              PSA
              <input
                name="internalPsa"
                type="number"
                step="0.01"
                className={style.winXpInputWrapper}
                value={internalFormValues.psa}
                onChange={(e) => {
                  setInternalFormValues((prev) => ({
                    psa: e.target.value,
                    date: prev.date,
                  }));
                }}
              />
            </label>
            <label className={style.psaLabel}>
              Date
              <div
                className={`${style.addPregnancyDatePickerWrapper} ${style.winXpDatepickerWrapper}`}
              >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    format="DD-MM-YYYY"
                    name="internalDate"
                    value={
                      internalFormValues.date
                        ? dayjs(internalFormValues.date, "DD-MM-YYYY")
                        : null
                    }
                    sx={{
                      width: "100%",
                      height: "36px",
                    }}
                    onChange={(newValue) => {
                      setInternalFormValues((prev) => ({
                        psa: prev.psa,
                        date: newValue
                          ? moment(new Date(dayjs(newValue))).format(
                              "DD-MM-YYYY"
                            )
                          : null,
                      }));
                    }}
                  />
                </LocalizationProvider>
              </div>
            </label>
            <button
              type="button"
              onClick={() => {
                if (!internalFormValues.psa || !internalFormValues.date) {
                  toast.error("Enter psa and date to add new item");
                  return;
                }

                append({
                  psa: internalFormValues.psa,
                  date: internalFormValues.date,
                });

                setInternalFormValues({
                  date: "",
                  psa: "",
                });
              }}
              className={style.addPsaTestButton}
            >
              Add
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
